// ProjectBrokenPage — the dedicated broken-manifest error page (RFC-0010 §8,
// PRD-0001 §1.6.3, Task 4.2) at /projects/:repo/broken.
//
// Per PRD-0001 §1.6.3 this surfaces the manifest parser/validation error
// VERBATIM plus links to the raw manifest and the file on GitHub. The last-good
// docs themselves keep rendering at /projects/:repo behind the
// ManifestBrokenBanner (ADR-0005); this page is where the error text lives.

import { Link, useParams } from 'react-router-dom';
import { WarningOctagon, GithubLogo, FileCode, ArrowLeft } from '@phosphor-icons/react';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/query-skeletons';
import { useProject } from '@/lib/queries/projects';
import { ApiError } from '@/lib/api-client';

const DEFAULT_MANIFEST = '.nmblr.portal.yaml';

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function ProjectBrokenPage() {
  const { repo } = useParams();
  const { data: project, isLoading, isError, error, refetch } = useProject(repo);

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading project" className="flex flex-col gap-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-10 w-1/2" />
      </div>
    );
  }

  if (isError || !project || !repo) {
    if (isNotFound(error)) {
      return (
        <div className="rounded-xl border border-border bg-surface shadow-(--c-shadow-sm)">
          <EmptyState
            icon={<FileCode size={22} />}
            title="Project not found"
            description="This repository isn't connected to the portal."
            action={
              <Link to="/repositories">
                <Button size="md">Back to repositories</Button>
              </Link>
            }
          />
        </div>
      );
    }
    return <ErrorPanel error={error} onRetry={() => void refetch()} />;
  }

  const manifest = project.manifestName ?? DEFAULT_MANIFEST;
  const blobUrl = `https://github.com/${project.fullName}/blob/${project.defaultBranch}/${manifest}`;
  const rawUrl = `https://github.com/${project.fullName}/raw/${project.defaultBranch}/${manifest}`;

  return (
    <>
      <PageHeader
        title="Manifest broken"
        description={`${project.name} — the ${manifest} manifest failed to parse or validate. Last-good documentation is still available.`}
        action={
          <Link to={`/projects/${encodeURIComponent(project.name)}`}>
            <Button size="md" variant="secondary">
              <ArrowLeft size={15} weight="bold" />
              View docs
            </Button>
          </Link>
        }
      />

      {/* Verbatim parser error (PRD-0001 §1.6.3). */}
      <section className="mb-6 rounded-xl border border-[rgba(255,92,92,0.25)] bg-error-bg p-5">
        <div className="mb-3 flex items-center gap-2 text-error">
          <WarningOctagon size={18} weight="fill" />
          <h2 className="text-[13px] font-semibold uppercase tracking-wide">Parser error</h2>
        </div>
        {project.manifestError ? (
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-surface p-4 font-mono text-[12.5px] leading-relaxed text-text">
            {project.manifestError}
          </pre>
        ) : (
          <p className="text-sm text-muted">
            No specific error message was recorded for this manifest.
          </p>
        )}
      </section>

      {/* Links to the raw manifest + the file on GitHub. */}
      <section className="flex flex-wrap gap-3">
        <a
          href={rawUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-[13px] font-medium text-text/80 transition-colors hover:bg-overlay/5 hover:text-text"
        >
          <FileCode size={15} className="shrink-0" />
          Raw manifest
        </a>
        <a
          href={blobUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-[13px] font-medium text-text/80 transition-colors hover:bg-overlay/5 hover:text-text"
        >
          <GithubLogo size={15} className="shrink-0" />
          View on GitHub
        </a>
      </section>
    </>
  );
}
